import { Alert, Anchor, Button, Select, SimpleGrid, Stack, Text, TextInput, Textarea, ThemeIcon, Title } from '@mantine/core';
import { CheckCircleIcon, PaperPlaneRightIcon, WarningIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { ApiError, errorMessage, http } from '../../../lib/api';

const STUDENT_BANDS = [
  { value: 'under-50', label: 'Under 50' },
  { value: '50-200', label: '50 to 200' },
  { value: '200-500', label: '200 to 500' },
  { value: '500-plus', label: 'More than 500' },
];

const EMPTY = { name: '', institute: '', phone: '', city: '', students: null as string | null, message: '', website: '' };

/** "Book a demo": stored for the Testpio team (Platform console → Demo requests). */
export function DemoForm() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [sentTo, setSentTo] = useState<{ name: string; phone: string } | null>(null);
  const set = (patch: Partial<typeof EMPTY>) => setForm((f) => ({ ...f, ...patch }));

  if (sentTo) {
    return (
      <Stack align="center" ta="center" gap="sm" py="xl" role="status">
        <ThemeIcon size={64} radius="xl" color="green" variant="light">
          <CheckCircleIcon size={34} weight="duotone" />
        </ThemeIcon>
        <Title order={3} mt="xs">
          Thank you, {sentTo.name.split(' ')[0]}
        </Title>
        <Text c="dimmed" maw={360}>
          We have your request and will get in touch on {sentTo.phone} to set up a time.
        </Text>
        <Button
          variant="subtle"
          mt="xs"
          onClick={() => {
            setForm(EMPTY);
            setSentTo(null);
          }}
        >
          Send another request
        </Button>
      </Stack>
    );
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        setFieldErrors({});
        http
          .post('/public/demo-requests', {
            name: form.name,
            institute: form.institute,
            phone: form.phone,
            city: form.city || undefined,
            students: form.students ?? undefined,
            message: form.message || undefined,
            website: form.website || undefined,
          })
          .then(() => setSentTo({ name: form.name.trim(), phone: form.phone.trim() }))
          .catch((err) => {
            if (err instanceof ApiError && err.code === 'VALIDATION' && Object.keys(err.fieldErrors).length > 0) setFieldErrors(err.fieldErrors);
            else setError(err instanceof ApiError && err.code === 'NETWORK' ? 'No internet connection. Please try again.' : errorMessage(err));
          })
          .finally(() => setBusy(false));
      }}
    >
      <Stack gap="md">
        <div>
          <Title order={3} fz={22} lh={1.25}>
            Book a demo
          </Title>
          <Text size="sm" c="dimmed" mt={6}>
            Takes a minute. We use these details only to contact you about Testpio (
            <Anchor href="/privacy" size="sm" inherit>
              privacy policy
            </Anchor>
            ).
          </Text>
        </div>
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md" verticalSpacing="md">
          <TextInput label="Your name" value={form.name} onChange={(e) => set({ name: e.currentTarget.value })} error={fieldErrors.name} required autoComplete="name" maxLength={80} size="md" />
          <TextInput
            label="Mobile number"
            value={form.phone}
            onChange={(e) => set({ phone: e.currentTarget.value })}
            error={fieldErrors.phone}
            required
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="98765 43210"
            maxLength={16}
            size="md"
          />
        </SimpleGrid>
        <TextInput label="Institute name" value={form.institute} onChange={(e) => set({ institute: e.currentTarget.value })} error={fieldErrors.institute} required autoComplete="organization" maxLength={120} size="md" />
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md" verticalSpacing="md">
          <TextInput label="City" value={form.city} onChange={(e) => set({ city: e.currentTarget.value })} error={fieldErrors.city} autoComplete="address-level2" maxLength={60} size="md" />
          <Select label="Number of students" data={STUDENT_BANDS} value={form.students} onChange={(v) => set({ students: v })} placeholder="Choose" clearable size="md" comboboxProps={{ withinPortal: true }} />
        </SimpleGrid>
        <Textarea label="Anything we should know?" value={form.message} onChange={(e) => set({ message: e.currentTarget.value })} error={fieldErrors.message} placeholder="Exams you prepare for, batch sizes, questions you have…" autosize minRows={3} maxRows={6} maxLength={1000} size="md" />
        {/* Hidden from people; only bots fill it. */}
        <div className="lp-honeypot" aria-hidden="true">
          <label>
            Website
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.currentTarget.value })} />
          </label>
        </div>
        {error && (
          <Alert color="red" icon={<WarningIcon size={20} weight="duotone" />} radius="md">
            {error}
          </Alert>
        )}
        <Button type="submit" size="lg" loading={busy} rightSection={<PaperPlaneRightIcon size={18} weight="fill" />} fullWidth>
          Request a demo
        </Button>
      </Stack>
    </form>
  );
}
